/**
 * HotelSearchForm — drop-in panel for the HomePage vertical switcher.
 *
 * Renders inside the existing white search-card. Mirrors the responsive
 * footprint of the flight form so the home page hero+widget rhythm is
 * preserved.
 *
 * On submit, POSTs `/api/hotels/search` and navigates the user to
 *   /hotels/results?sessionId=<id>&...
 * The results page is responsible for polling.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { CalendarDays, Search, Loader2, Star } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import CityPicker from './CityPicker';
import RoomsGuestsPopover from './RoomsGuestsPopover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pull rooms from URL (URI-encoded JSON array). Returns null when missing/invalid.
const _readRoomsFromUrl = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch { /* fall through */ }
  return null;
};

const _readRatingsFromUrl = (raw) => {
  if (!raw) return null;
  const arr = String(raw).split(',').map((n) => parseInt(n, 10)).filter((n) => n >= 1 && n <= 5);
  return arr.length ? arr : null;
};

const _HotelDatePicker = ({ label, date, onSelect, placeholder = 'Select', minDate }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full text-left" data-testid={`hotel-date-${label.toLowerCase()}-trigger`}>
          <p className="text-[9px] font-bold text-[#659A9B] uppercase tracking-wider mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {label}
          </p>
          <div className="flex items-start gap-2 mt-0.5">
            <CalendarDays className="w-4 h-4 text-[#1B375B] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-bold text-[#1B375B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {date ? format(new Date(date), 'dd MMM') : placeholder}
              </p>
              <p className="text-[10px] sm:text-xs text-[#659A9B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {date ? format(new Date(date), 'EEEE, yyyy') : 'Pick a date'}
              </p>
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date ? new Date(date) : undefined}
          onSelect={(d) => {
            if (d) {
              onSelect(format(d, 'yyyy-MM-dd'));
              setOpen(false);
            }
          }}
          disabled={(d) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (d < today) return true;
            if (minDate && d <= new Date(minDate)) return true;
            return false;
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

const _RatingPicker = ({ value, onChange }) => {
  // value: array of selected star ratings (1-5)
  const toggle = (n) => {
    if (value.includes(n)) {
      const next = value.filter((v) => v !== n);
      onChange(next.length === 0 ? [1, 2, 3, 4, 5] : next);
    } else {
      onChange([...value, n].sort());
    }
  };
  const all = value.length === 5;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="hotel-rating-picker">
      <span className="text-[10px] sm:text-xs text-[#659A9B] font-bold uppercase tracking-wider mr-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Rating:
      </span>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value.includes(n);
        return (
          <button
            key={n}
            type="button"
            onClick={() => toggle(n)}
            data-testid={`hotel-rating-star-${n}`}
            className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold transition-all ${
              active && !all
                ? 'bg-amber-500 text-white shadow'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {n}<Star className="w-2.5 h-2.5 fill-current" />
          </button>
        );
      })}
    </div>
  );
};

export const HotelSearchForm = ({ variant = 'desktop' }) => {
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const isMobile = variant === 'mobile';

  // Defaults pull from URL params first (Modify-search prefill), then
  // fall back to Mumbai metro + 7-day-out check-in.
  const [city, setCity] = useState(() => {
    const cityid = urlParams.get('cityid');
    const cityname = urlParams.get('cityname');
    if (cityid && cityname) {
      return {
        cityid,
        name: cityname.toUpperCase(),
        country: 'INDIA',
        type: 'MULTI_CITY_VICINITY',
        fullRegionName: cityname.toUpperCase(),
      };
    }
    return {
      cityid: '699261', name: 'MUMBAI', country: 'INDIA',
      type: 'MULTI_CITY_VICINITY', fullRegionName: 'MUMBAI, MAHARASHTRA, INDIA',
    };
  });
  const [checkin, setCheckin] = useState(() => urlParams.get('checkin') || format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [checkout, setCheckout] = useState(() => urlParams.get('checkout') || format(addDays(new Date(), 9), 'yyyy-MM-dd'));
  const [rooms, setRooms] = useState(() => _readRoomsFromUrl(urlParams.get('rooms')) || [{ numberOfAdults: 2, numberOfChild: 0, childAge: [] }]);
  const [ratings, setRatings] = useState(() => _readRatingsFromUrl(urlParams.get('ratings')) || [1, 2, 3, 4, 5]);
  const [nationality, setNationality] = useState(() => urlParams.get('nationality') || '106');
  const [nationalityList, setNationalityList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Lazy-load nationalities (small list, ~20 rows)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/hotels/nationalities?limit=250`);
        if (res.ok) setNationalityList(await res.json());
      } catch {/* fail-quiet */}
    })();
  }, []);

  // Keep checkout strictly > checkin
  useEffect(() => {
    if (checkin && checkout && new Date(checkout) <= new Date(checkin)) {
      setCheckout(format(addDays(new Date(checkin), 1), 'yyyy-MM-dd'));
    }
  }, [checkin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    setErrorMsg('');
    if (!city?.cityid) {
      setErrorMsg('Please choose a destination.');
      return;
    }
    if (!checkin || !checkout || new Date(checkout) <= new Date(checkin)) {
      setErrorMsg('Check-out must be after check-in.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        checkinDate: checkin,
        checkoutDate: checkout,
        cityid: String(city.cityid),
        nationality: String(nationality || '106'),
        roomInfo: rooms.map((r) => ({
          numberOfAdults: r.numberOfAdults,
          numberOfChild: r.numberOfChild || 0,
          ...((r.numberOfChild || 0) > 0 ? { childAge: r.childAge || [] } : {}),
        })),
        // Only send ratings if user has narrowed from the all-stars default
        ...(ratings.length < 5 ? { ratings } : {}),
        fsc: true,
      };
      const res = await axios.post(`${BACKEND_URL}/api/hotels/search`, payload, { timeout: 60000 });
      const data = res.data || {};
      if (!data.success) {
        const errMsg = data.error?.message || 'Hotel search is temporarily unavailable. Please retry.';
        setErrorMsg(errMsg);
        return;
      }
      // Navigate to the results page with all context the user might want
      // to see / modify mid-flight.
      const qs = new URLSearchParams({
        sessionId: data.session_id,
        cityid: city.cityid,
        cityname: city.name,
        checkin,
        checkout,
        nationality: String(nationality || '106'),
        rooms: encodeURIComponent(JSON.stringify(rooms)),
        ...(ratings.length < 5 ? { ratings: ratings.join(',') } : {}),
      });
      navigate(`/hotels/results?${qs.toString()}`);
    } catch (e) {
      const msg = e.response?.data?.error?.message || e.response?.data?.detail?.message || 'Network error. Please retry.';
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const _NationalitySelect = (
    <select
      value={nationality}
      onChange={(e) => setNationality(e.target.value)}
      data-testid="hotel-nationality-select"
      className="text-xs sm:text-sm font-bold text-[#1B375B] bg-transparent focus:outline-none cursor-pointer max-w-[140px] truncate"
      style={{ fontFamily: 'Manrope, sans-serif' }}
    >
      {nationalityList.length === 0 && <option value="106">Indian</option>}
      {nationalityList.map((n) => (
        <option key={n.countryid} value={n.countryid}>{n.countryname}</option>
      ))}
    </select>
  );

  /* ─── MOBILE ─── */
  if (isMobile) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-3 sm:p-4 space-y-2.5 sm:space-y-3" data-testid="hotel-search-form-mobile">
        <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3">
          <CityPicker label="Destination" selectedCity={city} onSelect={setCity} side="left" />
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3">
            <_HotelDatePicker label="Check-in" date={checkin} onSelect={setCheckin} />
          </div>
          <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3">
            <_HotelDatePicker label="Check-out" date={checkout} onSelect={setCheckout} minDate={checkin} />
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3">
          <RoomsGuestsPopover rooms={rooms} onChange={setRooms} />
        </div>
        <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <p className="text-[9px] font-bold text-[#659A9B] uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Nationality</p>
            {_NationalitySelect}
          </div>
          <_RatingPicker value={ratings} onChange={setRatings} />
        </div>
        {errorMsg && (
          <p className="text-xs text-red-500 font-semibold px-1" data-testid="hotel-search-error">{errorMsg}</p>
        )}
        <button
          onClick={handleSearch}
          disabled={submitting}
          data-testid="hotel-search-submit"
          className="w-full py-3 bg-[#1B375B] text-white rounded-xl font-bold text-sm hover:bg-[#659A9B] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-md"
          style={{ minHeight: '44px', fontFamily: 'Manrope, sans-serif' }}
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</> : <><Search className="w-4 h-4" /> Search Hotels</>}
        </button>
      </div>
    );
  }

  /* ─── DESKTOP ─── */
  return (
    <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-5" data-testid="hotel-search-form-desktop">
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr_1.5fr] gap-3">
        <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
          <CityPicker label="Destination" selectedCity={city} onSelect={setCity} side="left" />
        </div>
        <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
          <_HotelDatePicker label="Check-in" date={checkin} onSelect={setCheckin} />
        </div>
        <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
          <_HotelDatePicker label="Check-out" date={checkout} onSelect={setCheckout} minDate={checkin} />
        </div>
        <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
          <RoomsGuestsPopover rooms={rooms} onChange={setRooms} />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
            <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Nationality</p>
            {_NationalitySelect}
          </div>
          <_RatingPicker value={ratings} onChange={setRatings} />
        </div>

        <button
          onClick={handleSearch}
          disabled={submitting}
          data-testid="hotel-search-submit"
          className="px-7 py-3 bg-[#1B375B] text-white rounded-xl font-bold text-sm hover:bg-[#659A9B] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-md"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</> : <><Search className="w-4 h-4" /> Search Hotels</>}
        </button>
      </div>

      {errorMsg && (
        <p className="text-xs text-red-500 font-semibold mt-2" data-testid="hotel-search-error">{errorMsg}</p>
      )}
    </div>
  );
};

export default HotelSearchForm;
